import {
  Tabs,
  Card,
  Text,
  Group,
  Stack,
  Grid,
  Badge,
  Progress,
  Loader,
  Alert
} from '@mantine/core';
import {
  IconUsers,
  IconBuildingStore,
  IconExclamationCircle,
  IconChartBar
} from '@tabler/icons-react';
import { useLiveQuery } from '../../api';

const AdminStats = () => {
  // Live: seller registrations/updates and verification submissions/reviews
  // refresh the counters as they happen.
  const { data: stats, loading, error } = useLiveQuery('/api/v1/admin/stats', undefined, {
    topics: ['admin.sellers', 'admin.verifications'],
  });

  if (loading) {
    return (
      <Tabs.Panel value="stats" pt="xs">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Tabs.Panel>
    );
  }

  if (error) {
    return (
      <Tabs.Panel value="stats" pt="xs">
        <Alert
          icon={<IconExclamationCircle size="1rem" />}
          color="red"
          title="Error loading statistics"
        >
          {error.message}
        </Alert>
      </Tabs.Panel>
    );
  }

  const totalSellers = stats?.total_sellers || 0;
  const sellerPercentages = {
    approved: totalSellers ? (stats.approved_sellers / totalSellers) * 100 : 0,
    pending: totalSellers ? (stats.pending_sellers / totalSellers) * 100 : 0,
  };
  const sellerShareOfUsers = stats?.total_users
    ? ((totalSellers / stats.total_users) * 100).toFixed(1)
    : '0.0';

  return (
    <Tabs.Panel value="stats" pt="xs">
      <Grid>
        {/* Users Overview */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group align="apart" mb="xs">
              <Text fw={500} size="lg">
                <IconUsers size="1.2rem" style={{ marginRight: '0.5rem' }} />
                Users Overview
              </Text>
            </Group>

            <Stack gap="md">
              <Group align="apart">
                <Text size="sm" c="dimmed">Total Users</Text>
                <Badge variant="light" size="lg">{stats?.total_users || 0}</Badge>
              </Group>

              <Group align="apart">
                <Text size="sm" c="dimmed">Recent Registrations (30 days)</Text>
                <Badge variant="outline" color="green">{stats?.recent_users || 0}</Badge>
              </Group>

              <Group align="apart">
                <Text size="sm" c="dimmed">Active Subscriptions</Text>
                <Badge variant="outline" color="grape">{stats?.active_subscriptions || 0}</Badge>
              </Group>

              <Group align="apart">
                <Text size="sm" c="dimmed">Messages (total / last 30 days)</Text>
                <Text size="sm" fw={500}>
                  {stats?.total_messages || 0} / {stats?.recent_messages || 0}
                </Text>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Sellers Overview */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group align="apart" mb="xs">
              <Text fw={500} size="lg">
                <IconBuildingStore size="1.2rem" style={{ marginRight: '0.5rem' }} />
                Sellers Overview
              </Text>
            </Group>

            <Stack gap="md">
              <Group align="apart">
                <Text size="sm" c="dimmed">Total Sellers</Text>
                <Badge variant="light" size="lg">{totalSellers}</Badge>
              </Group>

              <div>
                <Group align="apart" mb={5}>
                  <Text size="sm">Approved Sellers</Text>
                  <Text size="sm">{stats?.approved_sellers || 0} ({sellerPercentages.approved.toFixed(1)}%)</Text>
                </Group>
                <Progress value={sellerPercentages.approved} size="sm" color="green" />
              </div>

              <div>
                <Group align="apart" mb={5}>
                  <Text size="sm">Pending Sellers</Text>
                  <Text size="sm">{stats?.pending_sellers || 0} ({sellerPercentages.pending.toFixed(1)}%)</Text>
                </Group>
                <Progress value={sellerPercentages.pending} size="sm" color="yellow" />
              </div>

              <Group align="apart">
                <Text size="sm" c="dimmed">Pending ID Verifications</Text>
                <Badge variant="outline" color="yellow">{stats?.pending_verifications || 0}</Badge>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Platform Overview */}
        <Grid.Col span={12}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group align="apart" mb="xs">
              <Text fw={500} size="lg">
                <IconChartBar size="1.2rem" style={{ marginRight: '0.5rem' }} />
                Platform Overview
              </Text>
            </Group>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Accounts</Text>
                  <Text size="xl" fw={700}>{stats?.total_users || 0}</Text>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Seller Percentage</Text>
                  <Text size="xl" fw={700}>{sellerShareOfUsers}%</Text>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Special Requests (total / pending)</Text>
                  <Text size="xl" fw={700}>
                    {stats?.total_requests || 0} / {stats?.pending_requests || 0}
                  </Text>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Pending Verifications</Text>
                  <Text size="xl" fw={700} c={stats?.pending_verifications > 0 ? 'orange' : 'green'}>
                    {stats?.pending_verifications || 0}
                  </Text>
                </Stack>
              </Grid.Col>
            </Grid>
          </Card>
        </Grid.Col>
      </Grid>
    </Tabs.Panel>
  );
};

export default AdminStats;
